/**
 * groupSettings 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('groupSettings:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const groupSettingsZh: TranslationDict = {

  // ======================== 群聊设置 / GroupSettings ========================
  'title': '群聊设置',
  'tabGeneral': '基本设置',
  'tabMembers': '成员管理',
  'tabSpeak': 'AI 发言限制',
  'tabExport': '导出记录',
  'groupName': '群聊名称',
  'nameFromChannel': '群名跟随渠道',
  'nameFromChannelHint': '打开后群名跟着渠道那边走（比如 QQ 群名）：渠道里改了会自动同步；想立刻同步一次就点右上「刷新」。手动改群名会自动关掉它。',
  'bio': '群简介',
  'bioHint': '资料卡上那句介绍；QQ 群那边写了「群简介」时，点右上「刷新」会拉过来覆盖这里（那边没写就保持这里的内容）。',
  'bioPlaceholder': '一句话介绍这个群…',
  'bioEmpty': '还没有群简介',
  'channelSection': '{label} 通道',
  'channelUnknown': '通道那边还没报过这个群——机器人在那儿收到一条消息后就有了。',
  'pullAppliedName': '群名已同步并保存',
  'pullAppliedBio': '群简介已同步并保存',
  'pullUpToDate': '已是最新，通道那边没有变化',
  'pullChannelInfoDone': '已拉取：{name}（{n} 人）',
  'pullChannelInfoEmpty': '没拉到——机器人还没在那个群里收到过消息，或通道不支持查群信息。',
  'announcement': '群公告',
  'announcementPlaceholder': '输入群公告...',
  'updateAnnouncement': '更新公告',
  'deleteAnnouncement': '删除公告',
  'noAnnouncement': '暂无公告',
  'dnd': '消息免打扰',
  'dndEnabled': '免打扰已开启',
  'dndCancel': '取消免打扰',
  'dndHint': '选择免打扰时长，期间不会收到该群消息通知',
  'dnd15min': '15 分钟',
  'dnd30min': '30 分钟',
  'dnd1hour': '1 小时',
  'dnd4hours': '4 小时',
  'dnd8hours': '8 小时',
  'dndForever': '永久',
  'dndCustomPlaceholder': '自定义分钟数...',
  'vectorAccel': '向量加速',
  'vectorAccelAiOwned': 'AI 群自动启用',
  'vectorAccelHybridSearch': '混合检索历史消息',
  'discovery': '发现与入群',
  'searchable': '可被搜索',
  'searchableHint': '开启后其他人可以在搜索里找到本群',
  'autoApproveJoin': '加群自动通过',
  'autoApproveJoinHint': '关闭后加群需要群主或管理员在申请列表里通过',
  'approveInvites': '成员邀请需审批',
  'approveInvitesHint': '开启后群成员的邀请要先经群主或管理员批准（群主/管理员的邀请免审）',
  'pendingApprovals': '待审批申请',
  'ownerLeaveHint': '群主需先转让群主身份',
  'leaveGroup': '退出群聊',
  'ownerCannotLeave': '群主无法退群（需先转让）',
  'disbandGroup': '解散群聊',
  'confirmDisband': '确定要解散此群聊吗？所有消息和成员数据将被清除，此操作不可撤销。',
  'memberCount': '成员',
  'invite': '邀请',
  'roleOwner': '群主',
  'roleAdmin': '管理员',
  'roleMember': '成员',
  'demoteToMember': '降级为成员',
  'promoteToAdmin': '提拔为管理员',
  'kick': '移出群聊',
  'aiManagedGroup': 'AI 自建群聊',
  'aiManagedDesc1': '此群由 AI 管理，不设发言限制。',
  'aiManagedDesc2': 'AI 之间可自由交流协作。',
  'speakLimit': '每分钟最多发言次数',
  'unlimited': '不限制',
  'perMinute': '条/分钟',
  'unlimitedLabel': '0（不限）',
  'speakWindow': '统计时间窗口（秒）',
  'preview': '效果预览',
  'speakPreviewPer': '每',
  'speakPreviewAllow': '秒内最多允许',
  'speakPreviewRounds': '轮 AI 对话',
  'speakPreviewBufferNote': '（每轮包含一次 AI 发言，预留 2x 余量保证对话完整）',
  'speakPreviewUnlimitedDesc': '不限制 AI 发言频率，对话链仅靠意愿分自然终结',
  'saveSpeakLimit': '保存发言限制',
  'exportFormat': '导出格式',
  'exportJsonDesc': '结构化数据',
  'exportTxtDesc': '易读文本',
  'exportHtmlDesc': '精美网页',
  'dateRange': '日期范围（可选）',
  'downloadExport': '下载导出文件',
  'confirmKick': '确定要将 ',
  'confirmKickEnd': ' 移出群聊？',
  'confirmLeave': '确定要退出此群聊？',
  'federationShare': '联邦共享',
  'clickToLoadPeers': '点击加载已连接的对等端',
  'noConnectedPeers': '暂无可用的已连接对等端',
  'addPeersHint1': '请先在',
  'addPeersHint2': '管理员面板 → 联邦',
  'addPeersHint3': '中添加并连接对等端，然后回到此处共享。',
  'sharedTo': '已共享给',
  'autoForward': '个对等端，消息将自动转发',
  'federationShareTo': '共享到对等端',
  'federationUnshareFrom': '取消共享',
  'federationPeerConnected': '已连接',
  'federationPeerDisconnected': '未连接',
  'federationShareHint': '共享后，群内消息将自动转发到已连接的对等端实例。',
  'federationNoPeers': '暂无可用的对等端',
  'federationNoPeersHint': '请先在管理面板中添加对等端。',
  'federationNoDisplayName': '需要先在管理面板设定「实例代号」才能共享。',
  'groupAvatar': '群头像',
  'avatarModeDefault': '默认图标',
  'avatarModeMembers': '成员头像排列',
  'avatarModeCustom': '自定义上传',
  'includeAiInAvatar': '包含 AI 头像',
  'includeAiInAvatarDesc': '关闭后只显示人类成员头像',
  'avatarPickerTitle': '选择头像来源',
  'customAvatar': '自定义头像',
  'avatarPickerFromSpace': '从个人空间选择',
  'avatarPickerFromSpaceDesc': '选择已上传的图片',
  'avatarPickerUploadNew': '上传新图片',
  'avatarPickerUploadNewDesc': '选图后裁剪上传',
  'avatarPickerOfficial': '从官方素材选择',
  'avatarPickerOfficialDesc': '团队自绘头像',
  'avatarPickerNoImages': '个人空间中暂无图片',
  'transferOwner': '转让群主',
  'transferOwnerTitle': '选择新群主',
  'transferOwnerHint': '转让后你将自动成为管理员',
  'confirmTransfer': '确定将群主转让给',
  'noTransferTarget': '无可转让的成员',
}

export const groupSettingsEn: TranslationDict = {
  'title': 'Group Settings',
  'tabGeneral': 'Basic Settings',
  'tabMembers': 'Member Management',
  'tabSpeak': 'AI Speak Limit',
  'tabExport': 'Export Records',
  'groupName': 'Group Name',
  'nameFromChannel': 'Follow the channel name',
  'nameFromChannelHint': 'When on, the group name follows the channel side (e.g. the QQ group name) — changes there sync automatically; press Refresh above to sync right now. Renaming it here turns this off.',
  'bio': 'Group intro',
  'bioHint': 'Shown on the group card; when the QQ group has an intro, Refresh above pulls it in and overwrites this (if the channel has none, this stays as you wrote it).',
  'bioPlaceholder': 'One line about this group…',
  'bioEmpty': 'No intro yet',
  'channelSection': '{label} channel',
  'channelUnknown': 'The channel has not reported this group yet — it shows up once the bot receives a message there.',
  'pullAppliedName': 'Group name synced and saved',
  'pullAppliedBio': 'Group intro synced and saved',
  'pullUpToDate': 'Already up to date',
  'pullChannelInfoDone': 'Pulled: {name} ({n} members)',
  'pullChannelInfoEmpty': 'Nothing yet — the bot has not received a message in that group, or the channel cannot report group info.',
  'announcement': 'Announcement',
  'announcementPlaceholder': 'Enter announcement...',
  'updateAnnouncement': 'Update Announcement',
  'deleteAnnouncement': 'Delete Announcement',
  'noAnnouncement': 'No announcement',
  'dnd': 'Message Mute / DND',
  'dndEnabled': 'DND is on',
  'dndCancel': 'Cancel DND',
  'dndHint': "Select DND duration; you won't receive notifications for this group",
  'dnd15min': '15 min',
  'dnd30min': '30 min',
  'dnd1hour': '1 hour',
  'dnd4hours': '4 hours',
  'dnd8hours': '8 hours',
  'dndForever': 'Forever',
  'dndCustomPlaceholder': 'Custom minutes...',
  'vectorAccel': 'Vector Acceleration',
  'vectorAccelAiOwned': 'Auto-enabled for AI groups',
  'vectorAccelHybridSearch': 'Hybrid search of historical messages',
  'discovery': 'Discovery and joining',
  'searchable': 'Searchable',
  'searchableHint': 'When on, others can find this group in search',
  'autoApproveJoin': 'Auto-approve joins',
  'autoApproveJoinHint': 'When off, joins wait for an owner or admin to approve them in the request list',
  'approveInvites': 'Approvals for member invites',
  'approveInvitesHint': 'When on, invites from members need an owner or admin to approve first (owner/admin invites are exempt)',
  'pendingApprovals': 'Pending approvals',
  'ownerLeaveHint': 'Owner must transfer ownership first',
  'leaveGroup': 'Leave Group',
  'ownerCannotLeave': 'Owner cannot leave (must transfer first)',
  'disbandGroup': 'Disband Group',
  'confirmDisband': 'Are you sure you want to disband this group? All messages and member data will be permanently deleted. This action cannot be undone.',
  'memberCount': 'Members',
  'invite': 'Invite',
  'roleOwner': 'Owner',
  'roleAdmin': 'Admin',
  'roleMember': 'Member',
  'demoteToMember': 'Demote to Member',
  'promoteToAdmin': 'Promote to Admin',
  'kick': 'Remove from Group',
  'aiManagedGroup': 'AI-Managed Group',
  'aiManagedDesc1': 'This group is managed by AI; no speak limits set.',
  'aiManagedDesc2': 'AI can freely communicate and collaborate.',
  'speakLimit': 'Max speak count per minute',
  'unlimited': 'No limit',
  'perMinute': 'per minute',
  'unlimitedLabel': '0 (unlimited)',
  'speakWindow': 'Stats window (seconds)',
  'preview': 'Preview',
  'speakPreviewPer': 'Every',
  'speakPreviewAllow': 'seconds, allow max',
  'speakPreviewRounds': 'rounds of AI dialogue',
  'speakPreviewBufferNote': '(Each round includes one AI utterance, 2x buffer for completeness)',
  'speakPreviewUnlimitedDesc': 'No AI speak limit; chains end naturally by will score',
  'saveSpeakLimit': 'Save Speak Limit',
  'exportFormat': 'Export Format',
  'exportJsonDesc': 'Structured data',
  'exportTxtDesc': 'Readable text',
  'exportHtmlDesc': 'Formatted HTML',
  'dateRange': 'Date Range (optional)',
  'downloadExport': 'Download Export',
  'confirmKick': 'Are you sure you want to remove ',
  'confirmKickEnd': ' from the group?',
  'confirmLeave': 'Are you sure you want to leave this group?',
  'federationShare': 'Federation Share',
  'clickToLoadPeers': 'Click to load connected peers',
  'noConnectedPeers': 'No connected peers available',
  'addPeersHint1': 'Please first go to ',
  'addPeersHint2': 'Admin Panel -> Federation ',
  'addPeersHint3': 'to add and connect peers, then return here to share.',
  'sharedTo': 'Shared with ',
  'autoForward': " peer(s); messages will auto-forward",
  'federationShareTo': 'Share to peer',
  'federationUnshareFrom': 'Unshare',
  'federationPeerConnected': 'Connected',
  'federationPeerDisconnected': 'Disconnected',
  'federationShareHint': 'When shared, group messages will auto-forward to connected peers.',
  'federationNoPeers': 'No peers available',
  'federationNoPeersHint': 'Please add peers in the admin panel first.',
  'federationNoDisplayName': 'Set "Instance Codename" in admin panel first before sharing.',
  'groupAvatar': 'Group Avatar',
  'avatarModeDefault': 'Default Icon',
  'avatarModeMembers': 'Member Avatars',
  'avatarModeCustom': 'Custom Upload',
  'includeAiInAvatar': 'Include AI Avatars',
  'includeAiInAvatarDesc': 'When off, only human avatars shown',
  'avatarPickerTitle': 'Choose Avatar Source',
  'customAvatar': 'Custom Avatar',
  'avatarPickerFromSpace': 'From My Files',
  'avatarPickerFromSpaceDesc': 'Select uploaded images',
  'avatarPickerUploadNew': 'Upload New Image',
  'avatarPickerUploadNewDesc': 'Crop after selecting',
  'avatarPickerOfficial': 'From Official Art',
  'avatarPickerOfficialDesc': "The team's own drawings",
  'avatarPickerNoImages': 'No images in your files yet',
  'transferOwner': 'Transfer Owner',
  'transferOwnerTitle': 'Select New Owner',
  'transferOwnerHint': 'You will become admin after transfer',
  'confirmTransfer': 'Transfer owner to',
  'noTransferTarget': 'No transferable members',
}

export const groupSettingsJa: TranslationDict = {
  'title': 'グループ設定',
  'tabGeneral': '基本設定',
  'tabMembers': 'メンバー管理',
  'tabSpeak': 'AI発言制限',
  'tabExport': '記録のエクスポート',
  'groupName': 'グループ名',
  'nameFromChannel': 'グループ名をチャンネルに合わせる',
  'nameFromChannelHint': 'オンにするとグループ名はチャンネル側（QQグループ名など）に従い、向こうで変われば自動で同期します。今すぐ同期したいときは右上の「更新」を押してください。ここで名前を変えると自動でオフになります。',
  'bio': 'グループ紹介',
  'bioHint': '資料カードに表示されます。QQグループに「グループ紹介」があれば、右上の「更新」で取り込んでここを上書きします（向こうに無ければここに書いた内容のままです）。',
  'bioPlaceholder': 'このグループを一言で…',
  'bioEmpty': '紹介はまだありません',
  'channelSection': '{label} チャンネル',
  'channelUnknown': 'チャンネル側からはまだ情報が届いていません。ボットがそこでメッセージを受け取ると表示されます。',
  'pullAppliedName': 'グループ名を同期して保存しました',
  'pullAppliedBio': 'グループ紹介を同期して保存しました',
  'pullUpToDate': 'すでに最新です',
  'pullChannelInfoDone': '取得しました：{name}（{n} 人）',
  'pullChannelInfoEmpty': '取得できませんでした。ボットがそのグループでまだメッセージを受け取っていないか、チャンネルがグループ情報に対応していません。',
  'announcement': 'お知らせ',
  'announcementPlaceholder': 'お知らせを入力...',
  'updateAnnouncement': 'お知らせを更新',
  'deleteAnnouncement': 'お知らせを削除',
  'noAnnouncement': 'お知らせはありません',
  'dnd': 'メッセージおやすみ',
  'dndEnabled': 'おやすみモード中',
  'dndCancel': 'おやすみ解除',
  'dndHint': 'おやすみ時間を選択すると、その間このグループの通知を受け取らなくなります',
  'dnd15min': '15分',
  'dnd30min': '30分',
  'dnd1hour': '1時間',
  'dnd4hours': '4時間',
  'dnd8hours': '8時間',
  'dndForever': '永続',
  'dndCustomPlaceholder': 'カスタム分数...',
  'vectorAccel': 'ベクター高速化',
  'vectorAccelAiOwned': 'AIグループで自動有効',
  'vectorAccelHybridSearch': '履歴メッセージのハイブリッド検索',
  'discovery': '発見と参加',
  'searchable': '検索に表示',
  'searchableHint': 'オンにすると他のユーザーが検索でこのグループを見つけられます',
  'autoApproveJoin': '参加を自動承認',
  'autoApproveJoinHint': 'オフにすると、参加はオーナーまたは管理者が申請一覧で承認するまで待ちます',
  'approveInvites': 'メンバーの招待に承認が必要',
  'approveInvitesHint': 'オンにするとメンバーの招待はオーナーまたは管理者の承認が必要です（オーナー/管理者の招待は不要）',
  'pendingApprovals': '承認待ちの申請',
  'ownerLeaveHint': 'グループオーナーは先に所有権を譲渡する必要があります',
  'leaveGroup': 'グループを退出',
  'ownerCannotLeave': 'オーナーは退出できません（先に譲渡してください）',
  'disbandGroup': 'グループを解散',
  'confirmDisband': 'このグループを解散してもよろしいですか？すべてのメッセージとメンバーデータが完全に削除され、元に戻せません。',
  'memberCount': 'メンバー',
  'invite': '招待',
  'roleOwner': 'オーナー',
  'roleAdmin': '管理者',
  'roleMember': 'メンバー',
  'demoteToMember': 'メンバーに降格',
  'promoteToAdmin': '管理者に昇格',
  'kick': 'グループから削除',
  'aiManagedGroup': 'AI管理グループ',
  'aiManagedDesc1': 'このグループはAIが管理しており、発言制限は設定されていません。',
  'aiManagedDesc2': 'AI同士が自由に会話・協働できます。',
  'speakLimit': '1分あたりの最大発言回数',
  'unlimited': '制限なし',
  'perMinute': '回/分',
  'unlimitedLabel': '0（無制限）',
  'speakWindow': '統計ウィンドウ（秒）',
  'preview': 'プレビュー',
  'speakPreviewPer': '毎',
  'speakPreviewAllow': '秒に最大',
  'speakPreviewRounds': 'ラウンドのAI対話を許可',
  'speakPreviewBufferNote': '（各ラウンドにAIの発言1回を含み、会話の完全性のため2倍の余裕を確保）',
  'speakPreviewUnlimitedDesc': 'AI発言を制限せず、意思スコアのみで自然終了します',
  'saveSpeakLimit': '発言制限を保存',
  'exportFormat': 'エクスポート形式',
  'exportJsonDesc': '構造化データ',
  'exportTxtDesc': '読みやすいテキスト',
  'exportHtmlDesc': '整ったHTML',
  'dateRange': '日付範囲（任意）',
  'downloadExport': 'エクスポートをダウンロード',
  'confirmKick': '本当に ',
  'confirmKickEnd': ' をグループから削除しますか？',
  'confirmLeave': '本当にこのグループを退出しますか？',
  'federationShare': '連合共有',
  'clickToLoadPeers': 'クリックして接続済みピアを読み込む',
  'noConnectedPeers': '利用可能な接続済みピアがありません',
  'addPeersHint1': '先に ',
  'addPeersHint2': '管理パネル → 連合 ',
  'addPeersHint3': 'でピアを追加・接続してから、ここで共有してください。',
  'sharedTo': '共有先 ',
  'autoForward': ' ピアに自動転送されます',
  'federationShareTo': 'ピアと共有',
  'federationUnshareFrom': '共有解除',
  'federationPeerConnected': '接続済み',
  'federationPeerDisconnected': '未接続',
  'federationShareHint': '共有すると、グループメッセージが接続済みのピアに自動転送されます。',
  'federationNoPeers': '利用可能なピアがありません',
  'federationNoPeersHint': '先に管理パネルでピアを追加してください。',
  'federationNoDisplayName': '共有するには、管理パネルで「インスタンスコードネーム」を設定してください。',
  'groupAvatar': 'グループアバター',
  'avatarModeDefault': 'デフォルトアイコン',
  'avatarModeMembers': 'メンバーアバター',
  'avatarModeCustom': 'カスタムアップロード',
  'includeAiInAvatar': 'AIアバターを含める',
  'includeAiInAvatarDesc': 'オフ：人間のみ表示',
  'avatarPickerTitle': 'アバターソースを選択',
  'customAvatar': 'カスタムアバター',
  'avatarPickerFromSpace': 'マイファイルから',
  'avatarPickerFromSpaceDesc': 'アップロード済み画像',
  'avatarPickerUploadNew': '新規画像をアップロード',
  'avatarPickerUploadNewDesc': '選択後にトリミング',
  'avatarPickerOfficial': '公式素材から選ぶ',
  'avatarPickerOfficialDesc': 'チーム描き下ろし',
  'avatarPickerNoImages': 'ファイルに画像がありません',
  'transferOwner': 'オーナー権限の譲渡',
  'transferOwnerTitle': '新しいオーナーを選択',
  'transferOwnerHint': '譲渡後は管理者になります',
  'confirmTransfer': 'オーナー権限を譲渡します:',
  'noTransferTarget': '譲渡可能なメンバーがいません',
}
